import { personalAccessToken } from '@/services/accessToken';
import { onRealtimeConnectionChange } from '@/services/realtimeSocket';
import { isRealtimeRefused, onRealtimeRefusedChange } from '@/services/realtimeClients';

/**
 * One tab's follow of a stream that belongs to the signed-in person rather than to a work area —
 * `myAttentionChanges` (F-0236) and `myWorkAreasChanges` (I-1525) — on the socket the tab already
 * has.
 *
 * Every follower shares one subscription. It is opened by the first follower and closed with the
 * last, reopened with a capped backoff when it fails or ends (a sign-out disposes the socket, and the
 * next sign-in needs a fresh subscription), and a reconnect tells every follower to re-read, because
 * a gap means missed signals.
 *
 * ⛔ **It opens only for a signed-in tab.** The stores join at start-up, before anybody may have
 * signed in; a subscription without a session is refused by the socket's door and would retry for
 * nothing. So it opens when a follower joins with a session in hand, and otherwise when `wake` is
 * called — which a follower does after a read that succeeded, the proof that the tab has a session.
 */
export interface PersonalStreamListener<T> {
  onChange?: (change: T) => void;
  /** The stream was down: re-read, because signals may have been missed. */
  onResume?: () => void;
}

export interface IPersonalStream<T> {
  subscribe(listener: PersonalStreamListener<T>): () => void;
  /** Open the stream if a follower wants it and the tab now has a session. Cheap when already open. */
  wake(): void;
  resetForTest(): void;
}

export type OpenPersonalStream<T> = (
  onChange: (change: T) => void,
  onError: (error: unknown) => void,
) => () => void;

const REOPEN_BASE_DELAY_MS = 1_000;
const REOPEN_MAX_DELAY_MS = 30_000;
/** I-2199: how often followers re-read while the socket is refused (4403) and no signal can come. */
export const PERSONAL_REFUSED_REFRESH_MS = 30_000;

function signedIn(): boolean {
  try {
    return Boolean(personalAccessToken());
  } catch {
    return false;
  }
}

export function createPersonalStream<T>(openStream: OpenPersonalStream<T>): IPersonalStream<T> {
  const listeners = new Set<PersonalStreamListener<T>>();
  let dispose: (() => void) | null = null;
  let reopenTimer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let missed = false;
  let stopWatchingConnection: (() => void) | null = null;
  let stopWatchingRefusal: (() => void) | null = null;
  let refusedTimer: ReturnType<typeof setInterval> | null = null;

  function stopRefusedRefresh(): void {
    if (refusedTimer) clearInterval(refusedTimer);
    refusedTimer = null;
  }

  // A refused socket delivers nothing and fails nothing, so the followers re-read on a timer.
  function syncRefusedRefresh(): void {
    if (!isRealtimeRefused() || listeners.size === 0) {
      stopRefusedRefresh();
      return;
    }
    if (refusedTimer) return;
    refusedTimer = setInterval(
      () => listeners.forEach((listener) => listener.onResume?.()),
      PERSONAL_REFUSED_REFRESH_MS,
    );
  }

  function resume(): void {
    if (!missed) return;
    missed = false;
    listeners.forEach((listener) => listener.onResume?.());
  }

  function open(): void {
    dispose = openStream(
      (change) => {
        attempt = 0;
        resume();
        listeners.forEach((listener) => listener.onChange?.(change));
      },
      () => scheduleReopen(),
    );
    // Reopening after a gap is itself the reason to re-read; it need not wait for the next signal.
    resume();
  }

  function openIfWanted(): void {
    if (listeners.size === 0 || dispose || reopenTimer || !signedIn()) return;
    open();
  }

  function scheduleReopen(): void {
    missed = true;
    dispose?.();
    dispose = null;
    if (reopenTimer || listeners.size === 0) return;
    const delay = Math.min(REOPEN_MAX_DELAY_MS, REOPEN_BASE_DELAY_MS * 2 ** attempt);
    attempt += 1;
    reopenTimer = setTimeout(() => {
      reopenTimer = null;
      openIfWanted();
    }, delay);
  }

  function close(): void {
    if (reopenTimer) clearTimeout(reopenTimer);
    reopenTimer = null;
    dispose?.();
    dispose = null;
    stopWatchingConnection?.();
    stopWatchingConnection = null;
    stopWatchingRefusal?.();
    stopWatchingRefusal = null;
    stopRefusedRefresh();
    attempt = 0;
    missed = false;
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      if (!stopWatchingConnection) {
        stopWatchingConnection = onRealtimeConnectionChange((connected) => {
          if (!connected) missed = true;
          else resume();
        });
      }
      if (!stopWatchingRefusal) stopWatchingRefusal = onRealtimeRefusedChange(syncRefusedRefresh);
      syncRefusedRefresh();
      openIfWanted();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) close();
      };
    },
    wake: openIfWanted,
    resetForTest() {
      listeners.clear();
      close();
    },
  };
}
